import { Suspense } from 'react';

import { PerformanceStatusPill } from '@/components/base/performance-status-pill';
import { SourcingStatusPill } from '@/components/base/sourcing-status-pill';
import { OpeningStatus } from '@/components/ui/opening-status';
import { Title } from '@/components/ui/title';
import {
  useOpeningContext,
  useOpeningDetails,
} from '@/contexts/opening-context';
import { useOpeningSlug } from '@/hooks/use-opening-slug';
import { formatShortDate } from '@/utils/date-util';

import { AutomationStatusPill } from './automation-status-pill';
import { HiringGoal } from './hiring-goal';
import { Tabs } from './tabs';

function OverviewHeaderContent() {
  const openingSlug = useOpeningSlug();
  const { opening, hiringGoal, analyticsData, hiringGoalEditUrl } =
    useOpeningDetails();
  const { metricsViewMode, displayPrefs, hiringGoalId } = useOpeningContext();
  const status = hiringGoal?.performance_status;

  return (
    <div className="flex flex-col gap-2 self-stretch">
      <div className="flex items-center gap-1">
        <OpeningStatus active={opening.isActive} />
        <Title title={opening.title} />
      </div>

      <div className="flex items-center gap-2">
        <SourcingStatusPill status={opening.sourcingStatus} />
        {metricsViewMode === 'hiring_goal' && status && (
          <PerformanceStatusPill
            status={status}
            openingSlug={openingSlug}
            hiringGoalId={hiringGoalId}
            showAggregateData={displayPrefs.showAggregateHires}
            hireActual={analyticsData.hire_stats.actual.total}
            applicantActual={analyticsData.applicants_stats.actual}
            startDate={
              hiringGoal?.start_date && formatShortDate(hiringGoal?.start_date)
            }
            endDate={
              hiringGoal?.hire_by && formatShortDate(hiringGoal?.hire_by)
            }
            duration={hiringGoal?.duration}
            manageHiringGoalLink={hiringGoalEditUrl}
          />
        )}
        {opening.hasActiveHiringGoal && (
          <AutomationStatusPill automation={opening.automation} />
        )}
      </div>

      <div className="mt-auto">
        <Tabs />
      </div>
    </div>
  );
}

/**
 * Wrapped in its own Suspense boundary so its `useOpeningDetails()` call
 * doesn't block `HiringGoal` (a sibling in `Overview`) from mounting and
 * firing its own fetches (e.g. AggregateHiresControls' hiring_openings
 * query) in parallel with this section's fetch.
 */
function OverviewHeader() {
  return (
    <Suspense fallback={<OverviewHeader.Skeleton />}>
      <OverviewHeaderContent />
    </Suspense>
  );
}

OverviewHeader.Skeleton = function OverviewHeaderSkeleton() {
  return (
    <div className="flex flex-col gap-2 self-stretch">
      <div className="flex items-center gap-2">
        <OpeningStatus.Skeleton />
        <Title.Skeleton />
      </div>
      <div className="flex items-center gap-2">
        <SourcingStatusPill.Skeleton />
        <PerformanceStatusPill.Skeleton />
        <AutomationStatusPill.Skeleton />
      </div>
      <div className="mt-auto">
        <Tabs />
      </div>
    </div>
  );
};

export function Overview() {
  return (
    <div className="shadow-base flex flex-col rounded border-b border-neutral-200 bg-white px-6 pt-5">
      <div className="flex flex-col items-start gap-6 lg:min-h-[150px] lg:flex-row lg:justify-between lg:gap-4">
        <OverviewHeader />
        <HiringGoal />
      </div>
    </div>
  );
}
